import { useQuery } from "@tanstack/react-query";

import { CommunityMessage } from "@/components/CommunityFeed/CommunityMessage";
import { getCommunityPostDocumentTitle } from "@/components/CommunityFeed/getCommunityPostDocumentTitle";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { CommunityPostViewCountTrackingProvider } from "@/providers/CommunityPostViewCountTrackingProvider";
import { useQuickReplyManager } from "@/providers/QuickReplyProvider";
import { StartupLoading } from "@/providers/StartupLoading";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { QuickReplyMenuBar } from "../components/QuickReplyMenuBar";

export function QuickReplyMessagePage(): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const { entityId: messageId } = useQuickReplyManager();

  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: message,
    isPending: isPendingMessage,
    error: errorMessage,
  } = useQuery({
    ...communityFeedQueries.messageDetails(messageId),
    enabled: Boolean(messageId),
  });

  useDocumentTitle(getCommunityPostDocumentTitle(errorMessage ? undefined : message, t("page.message-details.title")));

  if (isPendingMessage) {
    return <StartupLoading />;
  }

  if (errorMessage || !messageId) {
    return <ErrorPage status={(errorMessage as unknown as Response)?.status || 0} />;
  }

  return (
    <div className="flex min-h-full w-full flex-col items-center overflow-x-hidden">
      <QuickReplyMenuBar
        user={sessionUser}
        loginUrl={routes.communityFeed.postDetails({ slug: sessionUser.project.slug, id: messageId })}
      />
      <main className="w-full max-w-xl p-space-md">
        <CommunityPostViewCountTrackingProvider>
          <CommunityMessage isActionable={false} isExpanded {...{ message }} />
        </CommunityPostViewCountTrackingProvider>
      </main>
    </div>
  );
}
